import { useCallback, useEffect, useId, useRef } from "react";
import { X } from "lucide-react";
import { Button } from "./Button";
import { t } from "../i18n/t";

/**
 * §B7.2: a dialog traps focus, closes on Esc and returns focus to the element that opened it. It is a plain
 * `role="dialog" aria-modal="true"` element and not `<dialog>` — jsdom does not implement `showModal`, and a
 * primitive that cannot be tested is a primitive nobody trusts.
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  destructive = false,
  confirmDisabled = false,
}: {
  open: boolean;
  title: string;
  children: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  destructive?: boolean;
  confirmDisabled?: boolean;
}): JSX.Element | null {
  const titleId = useId();
  const panel = useRef<HTMLDivElement | null>(null);
  const opener = useRef<Element | null>(null);

  const focusables = useCallback((): HTMLElement[] => {
    const root = panel.current;
    if (root === null) return [];
    return Array.from(
      root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    );
  }, []);

  useEffect(() => {
    if (!open) return;
    opener.current = document.activeElement;
    // Initial focus lands on the primary action, not the header's close button: the action is what the dialog
    // asks about, and the close button remains one Tab away in the trap below.
    const preferred = panel.current?.querySelector<HTMLElement>(
      ".dialog__footer .button--primary, .dialog__footer .button--danger",
    );
    (preferred ?? focusables()[0])?.focus();
    return () => {
      (opener.current as HTMLElement | null)?.focus?.();
    };
  }, [open, focusables]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0] as HTMLElement;
      const last = items[items.length - 1] as HTMLElement;
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onCancel, focusables]);

  if (!open) return null;

  return (
    <div className="dialog__backdrop">
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={panel}>
        <div className="dialog__header">
          <h2 className="dialog__title" id={titleId}>
            {title}
          </h2>
          <Button className="dialog__close" aria-label={t("common.close")} onClick={onCancel}>
            <X size={18} strokeWidth={2} aria-hidden="true" />
          </Button>
        </div>
        <div className="dialog__body">{children}</div>
        <div className="dialog__footer">
          <Button onClick={onCancel}>{cancelLabel ?? t("common.cancel")}</Button>
          <Button variant={destructive ? "danger" : "primary"} onClick={onConfirm} disabled={confirmDisabled}>
            {confirmLabel ?? t("common.confirm")}
          </Button>
        </div>
      </div>
    </div>
  );
}
